import {useEffect, useMemo, useState} from "react";
import useLocalStorageModule from "use-local-storage";
import {useGetTotalConfig} from "../context/calculatorContext/context";
import {usePricingContext} from "../context/pricingContext/context";

const useLocalStorage = useLocalStorageModule.default ?? useLocalStorageModule;
const maxClusterSize = 6;

export const useHandlePlan = () => {
  const [selectedPlanCache, setSelectedPlanCache] = useLocalStorage(
    "selectedPlanCache",
    {}
  );
  const [selectedPlan, setSelectedPlan] = useState(selectedPlanCache);

  const {baremetalClusterPricing, baremetalPricing, cloudInstancePricing} =
    usePricingContext();
  const {ramInGB: requiredRAM} = useGetTotalConfig();

  const planOptions = useMemo(() => {
    const {plans: baremetalClusterPlans = []} = baremetalClusterPricing || {};
    const {plans: baremetalPlans = []} = baremetalPricing || {};
    const {plans: cloudInstancePlans = []} = cloudInstancePricing || {};

    const clusterOptions = [2, 3, 4, 5, 6];
    let allClusterPlans = [];
    clusterOptions.forEach((clusterSize) => {
      const calculatedBaremetalClusterPlans = baremetalClusterPlans.map((item) => ({
        ...item,
        clusterSize,
        config: {
          ...item.config,
          memory: item.config.memory * clusterSize,
          num_ssd: item.config.num_ssd * clusterSize,
          vcpu: item.config.vcpu * clusterSize,
        },
      }));
      allClusterPlans = [...allClusterPlans, ...calculatedBaremetalClusterPlans];
    });

    let plans = [];

    if (requiredRAM <= 64) {
      plans = cloudInstancePlans.filter((item) => item.config.memory >= requiredRAM);
      plans = [...plans, ...baremetalPlans, ...allClusterPlans];
    } else if (requiredRAM <= 1024) {
      plans = baremetalPlans.filter((item) => {
        if (item.config.memory_unit === "TB") {
          return item.config.memory * 1024 >= requiredRAM;
        }
        return item.config.memory >= requiredRAM;
      });
      plans = [...plans, ...allClusterPlans];
    } else if (requiredRAM <= 1024 * maxClusterSize) {
      plans = allClusterPlans.filter((item) => {
        if (item.config.memory_unit === "TB") {
          return item.config.memory * 1024 >= requiredRAM;
        }
        return item.config.memory >= requiredRAM;
      });
    }

    return [...plans, {name: "Custom", id: "custom"}];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requiredRAM]);

  const planType = useMemo(() => {
    let type = "Custom";
    if (selectedPlan?.id?.startsWith("BM-CL")) {
      type = "Baremetal-Cluster";
    } else if (selectedPlan?.id?.startsWith("BM")) {
      type = "Baremetal";
    } else if (selectedPlan?.id?.startsWith("CI")) {
      type = "Cloud-Instances";
    }
    return type;
  }, [selectedPlan]);

  useEffect(() => {
    // check if selectedPlanCache is in newOptions
    const index = planOptions.findIndex((option) => option.id === selectedPlanCache.id);
    if (index === -1) {
      // if not, set selectedPlan to first option and selectedPlanCache to first option
      const newPlan = planOptions[0] || {};
      setSelectedPlan(newPlan);
      setSelectedPlanCache(newPlan);
    } else {
      // if it is, set selectedPlan to selectedPlanCache
      setSelectedPlan(selectedPlanCache);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planOptions, selectedPlanCache]);

  const handlePlanChange = (plan) => {
    const selectedPlanOption = planOptions.find((option) => option.id === plan.id);
    const newPlan = selectedPlanOption || planOptions[0] || {};
    setSelectedPlan(newPlan);
    setSelectedPlanCache(newPlan);
  };

  return {handlePlanChange, planOptions, planType, selectedPlan};
};
